"use client";

import Link from "next/link";
import { useActionState, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

import { registerAction, type RegisterState } from "@/app/register/actions";
import { TurnstileWidget } from "@/components/turnstile-widget";
import { FORM_TOKEN_FIELD, HONEYPOT_FIELD } from "@/lib/auth/bot-check-fields";

const INPUT =
  "rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 read-only:bg-muted/40 read-only:text-muted-foreground";
const PRIMARY_BUTTON =
  "mt-1 inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60";

// The /register form. With an invite the email is the invited address and
// read-only (the action ignores the field and uses the invite's). If the
// invite stops working between loading and submitting, the action says so and
// the form carries on as a plain sign-up (pending staff approval).
//
// Bot protection (lib/auth/bot-check.ts): a signed render-time token, an
// off-screen honeypot field, and — when the server has Turnstile keys — a
// Cloudflare Turnstile widget that must be solved before submitting.
export function RegisterForm({
  invite,
  formToken,
  turnstileSiteKey,
}: {
  invite: { token: string; email: string } | null;
  formToken: string;
  turnstileSiteKey: string | null;
}) {
  const t = useTranslations("register");
  const locale = useLocale();
  const [state, formAction, pending] = useActionState<RegisterState, FormData>(registerAction, {});
  const activeInvite = state.dropInvite ? null : invite;
  // Which form instance (nonce) has a solved captcha: a re-keyed form starts
  // unsolved without an effect to reset it.
  const nonce = state.nonce ?? 0;
  const [solvedNonce, setSolvedNonce] = useState<number | null>(null);
  const [captchaLoadError, setCaptchaLoadError] = useState(false);
  const captchaReady = !turnstileSiteKey || solvedNonce === nonce;

  if (state.done) {
    return (
      <div role="status" className="flex flex-col gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm">
        <p className="font-medium text-emerald-800 dark:text-emerald-300">{t("pendingTitle")}</p>
        <p className="text-emerald-800 dark:text-emerald-300">{t("pendingBody")}</p>
        <Link href="/login" className="font-medium text-primary hover:underline">
          {t("signIn")}
        </Link>
      </div>
    );
  }

  const v = state.values;
  return (
    // Keyed by the response, so a failed submit re-renders with what was typed.
    <form key={nonce} action={formAction} className="relative flex flex-col gap-4">
      {activeInvite && <input type="hidden" name="invite" value={activeInvite.token} />}
      <input type="hidden" name={FORM_TOKEN_FIELD} value={state.formToken ?? formToken} />
      {/* Honeypot: off-screen rather than display:none, so naive bots still fill it. */}
      <div aria-hidden="true" className="absolute -left-[10000px] top-0 h-px w-px overflow-hidden">
        <label htmlFor={HONEYPOT_FIELD}>{t("honeypotLabel")}</label>
        <input id={HONEYPOT_FIELD} name={HONEYPOT_FIELD} type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
      </div>
      <Field id="name" label={t("nameLabel")}>
        <input id="name" name="name" autoComplete="name" required maxLength={200} defaultValue={v?.name} autoFocus className={INPUT} />
      </Field>
      <Field id="email" label={t("emailLabel")} hint={activeInvite ? t("invite.emailFixed") : undefined}>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="username"
          required
          maxLength={320}
          readOnly={!!activeInvite}
          defaultValue={activeInvite ? activeInvite.email : v?.email}
          className={INPUT}
        />
      </Field>
      <Field id="companyName" label={t("companyLabel")}>
        <input
          id="companyName"
          name="companyName"
          autoComplete="organization"
          required
          maxLength={200}
          defaultValue={v?.companyName}
          className={INPUT}
        />
      </Field>
      {!activeInvite && (
        // Optional (migration 056): a company already here that the registrant
        // works with. Its leads may approve the registration instead of our team.
        <Field id="partnerName" label={t("partnerLabel")} hint={t("partnerHint")}>
          <input
            id="partnerName"
            name="partnerName"
            autoComplete="off"
            maxLength={200}
            defaultValue={v?.partnerName}
            className={INPUT}
          />
        </Field>
      )}
      <Field id="password" label={t("passwordLabel")} hint={t("passwordHint", { min: 8 })}>
        <input id="password" name="password" type="password" autoComplete="new-password" required minLength={8} maxLength={256} className={INPUT} />
      </Field>
      <Field id="confirmPassword" label={t("confirmLabel")}>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          maxLength={256}
          className={INPUT}
        />
      </Field>
      {!activeInvite && <p className="text-xs text-muted-foreground">{t("approvalNote")}</p>}
      {turnstileSiteKey && (
        <div className="flex flex-col gap-1.5">
          <TurnstileWidget
            siteKey={turnstileSiteKey}
            action="register"
            language={locale}
            onSolvedChange={(solved) => setSolvedNonce(solved ? nonce : null)}
            onLoadError={() => setCaptchaLoadError(true)}
          />
          {captchaLoadError && <p className="text-xs text-red-600 dark:text-red-400">{t("captchaLoadError")}</p>}
        </div>
      )}
      {state.error && (
        <p
          role="alert"
          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-400"
        >
          {state.error}
        </p>
      )}
      <button type="submit" disabled={pending || !captchaReady} className={PRIMARY_BUTTON}>
        {pending ? t("submitting") : t("submit")}
      </button>
    </form>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
